import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useState } from "react";
import {
    KeyboardAvoidingView,
    Platform,
    StyleSheet,
    TextInput,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { useAuth } from "@/lib/auth";
import { addNumberRequest } from "@/lib/leads-api";
import { colors, fonts, gradients, radius, spacing } from "@/lib/theme";

/**
 * "I already have an account, add this number." The nurse enters the phone the
 * account was opened with + their name; we email back-office (Nir + Noel) who
 * verify and attach the new number by hand. (Per product: manual for now.)
 */
export default function AddNumberScreen() {
    const router = useRouter();
    const { phone } = useLocalSearchParams<{ phone: string }>();
    const { pendingPhone } = useAuth();
    const newPhone = phone || pendingPhone || "";

    const [existingPhone, setExistingPhone] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState(false);

    const canSubmit = existingPhone.trim().length >= 7 && firstName.trim().length > 0;

    const submit = async () => {
        if (!canSubmit || submitting) {
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            await addNumberRequest({
                existingPhone: existingPhone.trim(),
                newPhone,
                firstName: firstName.trim(),
                lastName: lastName.trim() || undefined
            });
            setDone(true);
        } catch (e) {
            setError(e instanceof Error ? e.message : "Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <SafeAreaView style={styles.container}>
                <View style={styles.doneContent}>
                    <MaterialCommunityIcons name="check-circle" size={64} color={colors.teal600} />
                    <Text style={styles.doneTitle}>Request received</Text>
                    <Text style={styles.doneSubtitle}>
                        Thanks! Our team will verify your account and add this number. We&apos;ll
                        text you once it&apos;s linked.
                    </Text>
                    <TouchableOpacity onPress={() => router.replace("/(auth)/login")}>
                        <Text style={styles.link}>Back to login</Text>
                    </TouchableOpacity>
                </View>
            </SafeAreaView>
        );
    }

    return (
        <SafeAreaView style={styles.container}>
            <KeyboardAvoidingView
                behavior={Platform.OS === "ios" ? "padding" : undefined}
                style={styles.flex}
            >
                <View style={styles.content}>
                    <Text style={styles.title}>Add this number to your account</Text>
                    <Text style={styles.subtitle}>
                        Enter the phone number your account was opened with and your name. Our team
                        will verify and add this new number for you.
                    </Text>

                    <Text style={styles.label}>Original account phone number *</Text>
                    <TextInput
                        style={styles.input}
                        value={existingPhone}
                        onChangeText={setExistingPhone}
                        placeholder="(555) 555-5555"
                        placeholderTextColor={colors.textSecondary}
                        keyboardType="phone-pad"
                    />

                    <Text style={styles.label}>First name *</Text>
                    <TextInput
                        style={styles.input}
                        value={firstName}
                        onChangeText={setFirstName}
                        placeholder="First name"
                        placeholderTextColor={colors.textSecondary}
                    />

                    <Text style={styles.label}>Last name</Text>
                    <TextInput
                        style={styles.input}
                        value={lastName}
                        onChangeText={setLastName}
                        placeholder="Last name"
                        placeholderTextColor={colors.textSecondary}
                    />

                    {error ? <Text style={styles.error}>{error}</Text> : null}

                    <TouchableOpacity
                        activeOpacity={0.85}
                        disabled={!canSubmit || submitting}
                        onPress={submit}
                        style={{
                            opacity: canSubmit && !submitting ? 1 : 0.5,
                            marginTop: spacing.lg
                        }}
                    >
                        <LinearGradient
                            colors={gradients.brand}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 0 }}
                            style={styles.button}
                        >
                            <Text style={styles.buttonText}>
                                {submitting ? "Sending…" : "Send request"}
                            </Text>
                        </LinearGradient>
                    </TouchableOpacity>

                    <TouchableOpacity onPress={() => router.back()} style={styles.backBtn}>
                        <Text style={styles.link}>Back</Text>
                    </TouchableOpacity>
                </View>
            </KeyboardAvoidingView>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    flex: { flex: 1 },
    content: { flex: 1, paddingHorizontal: spacing.lg, paddingTop: spacing.xl },
    title: { fontFamily: fonts.bold, fontSize: 24, color: colors.textPrimary },
    subtitle: {
        fontFamily: fonts.regular,
        fontSize: 14,
        color: colors.textSecondary,
        marginTop: spacing.xs,
        marginBottom: spacing.lg
    },
    label: {
        fontFamily: fonts.medium,
        fontSize: 13,
        color: colors.textPrimary,
        marginTop: spacing.md,
        marginBottom: spacing.xs
    },
    input: {
        fontFamily: fonts.regular,
        fontSize: 16,
        color: colors.textPrimary,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: radius.md,
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.sm,
        backgroundColor: colors.surface
    },
    button: {
        paddingVertical: spacing.md,
        borderRadius: radius.full,
        alignItems: "center"
    },
    buttonText: { fontFamily: fonts.bold, fontSize: 16, color: "#fff" },
    backBtn: { alignItems: "center", marginTop: spacing.lg },
    link: { fontFamily: fonts.medium, fontSize: 15, color: colors.teal600 },
    error: { fontFamily: fonts.regular, fontSize: 13, color: colors.error, marginTop: spacing.md },
    doneContent: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: spacing.xl,
        gap: spacing.md
    },
    doneTitle: { fontFamily: fonts.bold, fontSize: 22, color: colors.textPrimary },
    doneSubtitle: {
        fontFamily: fonts.regular,
        fontSize: 15,
        color: colors.textSecondary,
        textAlign: "center"
    }
});
